import { useRef, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, ChatCircleDots, Eye, Flag, Headphones, Kanban, ListNumbers, Lock, Microphone, Play, ShieldCheck, SpeakerHigh, UsersThree } from "@phosphor-icons/react";
import { ThemeToggle } from "@/components/observer/ThemeToggle";
import { SITUATIONS } from "@/lib/situations";
import { btnGhost, btnPrimary, labelCls } from "@/lib/ui";

// What a visitor sees at "/" before signing in. Everything here describes what the product does today.

const TURNS = [
  ["Priya", "#F59E0B", "If we move to the new tiers in Q1 we lose maybe two of the enterprise accounts, but the mid-market numbers more than cover it."],
  ["Daniel", "#10B981", "Finance modelled that on a 4% churn. I think that's optimistic."],
  ["Priya", "#F59E0B", "It's what we saw last year."],
  ["Meera", "#60A5FA", "Last year we hadn't raised prices. Can we agree the churn number before we decide the tiers?"],
];

const STEPS = [
  { n: "01", title: "Brief it", text: "A title, and what you want from it: settle a disagreement, check the numbers, widen the discussion. One box. People and background are optional." },
  { n: "02", title: "Let it listen", text: "In the room through a laptop mic, or in an online call through the meeting tab. Tell the room it is there; it transcribes as you go and keeps no recording." },
  { n: "03", title: "Ask it", text: "Type a question, or let it raise a hand at an interval you set. It answers with grounds from the transcript, or says it has nothing to add." },
];

const FEATURES = [
  { icon: Kanban, title: "A live discussion board", text: "Anchor, facts, assumptions, constraints, options, agreed, open. Inferred as the meeting runs, so it can spot an option that breaks a constraint or an assumption being treated as fact." },
  { icon: ListNumbers, title: "Chapters as it goes", text: "A short summary every ten minutes, so a two-hour meeting still has a shape and a question at the end can reach the start." },
  { icon: ChatCircleDots, title: "Answers that cite the room", text: "Each answer quotes the turns it rests on, with timestamps, and says what data it was missing. A blank is a blank, not filler." },
  { icon: Flag, title: "A quiet hand", text: "On a timer, it checks the last stretch for something worth raising and shows one flag at a time. Ignore it and it goes away." },
  { icon: SpeakerHigh, title: "Read aloud, if you want", text: "A firm, clear voice for when the room should hear it rather than read it. Off by default; one press per answer." },
  { icon: UsersThree, title: "Who said what", text: "Speaker names from the transcription, corrected by you mid-meeting, and rechecked across the whole recording at the end." },
  { icon: Headphones, title: "In the room or online", text: "A laptop in the meeting room, or a shared tab in Meet, Teams or Zoom. It keeps listening while you present slides." },
  { icon: Lock, title: "Private mode", text: "For a meeting that must not be kept: nothing is written to disk, the transcript lives in memory and is wiped at End." },
];

const Mock = () => (
  <div className="border border-[var(--o-border)] bg-[var(--o-bg2)] text-left rise" style={{ animationDelay: "200ms" }} data-testid="landing-mock" aria-hidden="true">
    <div className="flex items-center gap-3 px-4 py-2.5 border-b border-[var(--o-border)] font-mono text-[11px] text-[var(--o-muted2)]">
      <span className="relative flex h-2 w-2"><span className="listen-ring absolute inline-flex h-full w-full rounded-full bg-[#10B981]" /><span className="relative inline-flex h-2 w-2 rounded-full bg-[#10B981]" /></span>
      Listening · 00:42:10
      <span className="ml-auto text-[var(--o-fg)] truncate">Pricing review · H2</span>
    </div>
    <div className="grid md:grid-cols-[1.15fr_1fr]">
      <div className="p-4 space-y-3 border-b md:border-b-0 md:border-r border-[var(--o-border)]">
        {TURNS.map(([who, color, text], i) => (
          <div key={i} className="text-[13px] leading-relaxed">
            <span className="font-semibold mr-2" style={{ color }}>{who}</span>
            <span className="text-[var(--o-fg2)]">{text}</span>
          </div>
        ))}
        <div className="flex items-end gap-[3px] text-[var(--o-muted2)] pt-1">
          {[0, 0.15, 0.3, 0.45, 0.6].map((d) => <span key={d} className="eq-bar" style={{ animationDelay: `${d}s` }} />)}
        </div>
      </div>
      <div className="p-4 space-y-3">
        <div className="border border-[var(--o-border)] px-3 py-2 text-[13px] text-[var(--o-muted)] flex items-center justify-between">
          <span>Are we deciding on solid numbers?</span>
          <ArrowRight size={13} />
        </div>
        <div className="border border-[var(--o-border-strong)] bg-[var(--o-bg)] p-3.5 space-y-2.5">
          <div className="font-mono text-[10px] uppercase tracking-[0.16em] text-[var(--o-warn)]">Assumption treated as fact</div>
          <p className="text-[13px] leading-relaxed">The tier decision rests on 4% churn. Daniel called it optimistic and Meera pointed out last year had no price rise; nobody has answered that yet. Settle the churn number first: the tiers follow from it either way.</p>
          <div className="font-mono text-[10px] text-[var(--o-muted3)] space-y-0.5">
            <div>[00:39:40] Daniel: modelled that on a 4% churn…</div>
            <div>[00:41:52] Meera: last year we hadn't raised prices…</div>
          </div>
        </div>
      </div>
    </div>
  </div>
);

// Forty-four seconds with a voice-over. Nothing is fetched until it is played: the poster is a 50 KB still and the
// film itself is 2.2 MB at 720p, so it costs a visitor nothing to scroll past.
const Film = () => {
  const ref = useRef(null);
  const [playing, setPlaying] = useState(false);
  const play = () => {
    ref.current?.play().then(() => setPlaying(true)).catch(() => {});
  };
  return (
    <div className="relative border border-[var(--o-border)] bg-black aspect-video overflow-hidden" data-testid="landing-film">
      <video
        ref={ref}
        className="h-full w-full"
        poster="/media/observer-promo-poster.jpg"
        preload="none"
        playsInline
        controls={playing}
        onPlay={() => setPlaying(true)}
        onEnded={() => setPlaying(false)}
        data-testid="landing-video"
      >
        <source src="/media/observer-promo.mp4" type="video/mp4" />
        <source src="/media/observer-promo.webm" type="video/webm" />
        Your browser can't play this video.
      </video>
      {!playing && (
        <button type="button" onClick={play} className="absolute inset-0 flex items-center justify-center group" aria-label="Play the film" data-testid="landing-play">
          <span className="flex items-center gap-3 bg-[var(--o-fg)] text-[var(--o-bg)] px-6 py-3.5 text-sm font-semibold group-hover:opacity-90 transition-opacity">
            <Play size={16} weight="fill" /> Watch · 44 seconds
          </span>
        </button>
      )}
    </div>
  );
};

export default function Landing() {
  return (
    <div className="noise min-h-screen bg-[var(--o-bg)] text-[var(--o-fg)]" data-testid="landing-page">
      <header className="relative z-10 h-16 flex items-center justify-between px-5 sm:px-10 border-b border-[var(--o-border)] bg-[var(--o-bg)]">
        <div className="flex items-center gap-3">
          <Eye size={22} weight="bold" />
          <span className="font-display text-base sm:text-lg font-extrabold tracking-tight whitespace-nowrap">GoNav.Tech - Observer</span>
        </div>
        <nav className="flex items-center gap-1 sm:gap-2">
          <a href="#how" className="hidden sm:block px-3 py-2 text-sm text-[var(--o-muted)] hover:text-[var(--o-fg)]">How it works</a>
          <Link to="/privacy" className="px-3 py-2 text-sm text-[var(--o-muted)] hover:text-[var(--o-fg)]" data-testid="landing-privacy">Privacy</Link>
          <ThemeToggle />
          <Link to="/login" className={`${btnPrimary} ml-1 !py-2`} data-testid="landing-signin">Sign in <ArrowRight size={14} weight="bold" /></Link>
        </nav>
      </header>

      <main className="relative z-10">
        <section className="grid-lines border-b border-[var(--o-border)]">
          <div className="max-w-6xl px-5 sm:px-10 pt-16 sm:pt-24 pb-16 grid lg:grid-cols-[1fr_1.1fr] gap-10 lg:gap-14 items-center">
            <div className="rise">
              <p className={labelCls}>An AI observer for meetings</p>
              <h1 className="font-display text-5xl sm:text-6xl lg:text-7xl font-black leading-[0.95] tracking-tight mt-4">A thinking partner<br />in the room.</h1>
              <p className="text-base sm:text-lg text-[var(--o-muted)] mt-6 max-w-lg leading-relaxed">
                Observer listens to your meeting, holds the context, and speaks only when asked: the question nobody put, the number that doesn't add up, the option that breaks a constraint you agreed an hour ago.
              </p>
              <div className="flex flex-wrap gap-3 mt-8">
                <Link to="/login" className={`${btnPrimary} px-7 py-3.5`} data-testid="landing-hero-signin">Sign in <ArrowRight size={16} weight="bold" /></Link>
                <Link to="/privacy" className={`${btnGhost} px-6 py-3.5`}><ShieldCheck size={16} /> What it hears and keeps</Link>
              </div>
              <p className="font-mono text-[11px] text-[var(--o-muted3)] mt-6">Not a recorder. Audio is deleted as it is transcribed. Runs on your own server with your own model accounts.</p>
            </div>
            <Mock />
          </div>
        </section>

        <section id="film" className="border-b border-[var(--o-border)]">
          <div className="max-w-6xl px-5 sm:px-10 py-16">
            <div className="flex flex-wrap items-end justify-between gap-4 mb-8">
              <div>
                <p className={labelCls}>See it</p>
                <h2 className="font-display text-3xl sm:text-4xl font-black tracking-tight mt-3">A meeting, with the Observer in it.</h2>
              </div>
              <p className="text-sm text-[var(--o-muted)] max-w-md">Brief it in one line, let it listen, ask it when the room goes round in circles. With sound.</p>
            </div>
            <Film />
          </div>
        </section>

        <section id="how" className="border-b border-[var(--o-border)]">
          <div className="max-w-6xl px-5 sm:px-10 py-16">
            <p className={labelCls}>How it works</p>
            <h2 className="font-display text-3xl sm:text-4xl font-black tracking-tight mt-3">Three steps. No form to fill.</h2>
            <div className="grid md:grid-cols-3 gap-px bg-[var(--o-border)] border border-[var(--o-border)] mt-10">
              {STEPS.map((s) => (
                <div key={s.n} className="bg-[var(--o-bg)] p-6 sm:p-8" data-testid="landing-step">
                  <div className="font-mono text-xs text-[var(--o-muted3)]">{s.n}</div>
                  <h3 className="font-display text-xl font-bold mt-2">{s.title}</h3>
                  <p className="text-sm text-[var(--o-muted)] mt-3 leading-relaxed">{s.text}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="border-b border-[var(--o-border)]">
          <div className="max-w-6xl px-5 sm:px-10 py-16">
            <p className={labelCls}>Made for</p>
            <h2 className="font-display text-3xl sm:text-4xl font-black tracking-tight mt-3">The meetings that go in circles.</h2>
            <p className="text-sm sm:text-base text-[var(--o-muted)] mt-4 max-w-2xl">Tell it what the meeting is for and it reads the room against that. These are the starting points on the start page; you can write your own.</p>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 mt-10">
              {SITUATIONS.map(([label, text]) => (
                <div key={label} className="border border-[var(--o-border)] p-5 bg-[var(--o-bg2)]" data-testid="landing-situation">
                  <div className="font-display text-lg font-bold">{label}</div>
                  <p className="text-sm text-[var(--o-muted)] mt-2 leading-relaxed">{text}</p>
                </div>
              ))}
              <div className="border border-dashed border-[var(--o-border)] p-5 flex flex-col justify-center">
                <div className="font-display text-lg font-bold text-[var(--o-muted)]">Your own</div>
                <p className="text-sm text-[var(--o-muted2)] mt-2 leading-relaxed">“Help us settle the pricing disagreement without anyone feeling steamrolled.”</p>
              </div>
            </div>
          </div>
        </section>

        <section className="border-b border-[var(--o-border)]">
          <div className="max-w-6xl px-5 sm:px-10 py-16">
            <p className={labelCls}>What you get</p>
            <h2 className="font-display text-3xl sm:text-4xl font-black tracking-tight mt-3">Built from real meetings, not demos.</h2>
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-px bg-[var(--o-border)] border border-[var(--o-border)] mt-10">
              {FEATURES.map(({ icon: Icon, title, text }) => (
                <div key={title} className="bg-[var(--o-bg)] p-6" data-testid="landing-feature">
                  <Icon size={20} className="text-[var(--o-muted)]" />
                  <h3 className="font-display text-base font-bold mt-4">{title}</h3>
                  <p className="text-[13px] text-[var(--o-muted)] mt-2 leading-relaxed">{text}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="border-b border-[var(--o-border)] bg-[var(--o-bg2)]">
          <div className="max-w-6xl px-5 sm:px-10 py-16 grid lg:grid-cols-[1fr_1fr] gap-10 items-center">
            <div>
              <p className={labelCls}>Privacy</p>
              <h2 className="font-display text-3xl sm:text-4xl font-black tracking-tight mt-3">It is not a recorder.</h2>
              <p className="text-sm sm:text-base text-[var(--o-muted)] mt-4 leading-relaxed">
                Audio reaches the server in pieces of under a minute and each piece is deleted once transcribed. Meetings are files on your own server that only the person who ran them and admins can open, and either can delete them. The AI models run on your own API accounts; nothing goes anywhere else, and nothing is used to improve the Observer.
              </p>
              <Link to="/privacy" className={`${btnGhost} mt-6`} data-testid="landing-privacy-more"><ShieldCheck size={15} /> Every question, answered plainly</Link>
            </div>
            <ul className="space-y-3">
              {[
                [Microphone, "Listens only between Start and End, in the tab you started it in."],
                [Lock, "Private mode keeps nothing on disk; the transcript is wiped at End."],
                [UsersThree, "You tell the room. The privacy page has the one line to say."],
                [ShieldCheck, "Personal accounts, salted password hashes, an activity log admins can read."],
              ].map(([Icon, text]) => (
                <li key={text} className="flex items-start gap-3 border border-[var(--o-border)] bg-[var(--o-bg)] px-4 py-3 text-sm">
                  <Icon size={16} className="mt-0.5 shrink-0 text-[var(--o-muted)]" /> <span>{text}</span>
                </li>
              ))}
            </ul>
          </div>
        </section>

        <section>
          <div className="max-w-6xl px-5 sm:px-10 py-20 text-center">
            <h2 className="font-display text-3xl sm:text-5xl font-black tracking-tight">Bring it to the next meeting.</h2>
            <p className="text-sm sm:text-base text-[var(--o-muted)] mt-4 max-w-xl mx-auto">Accounts are made by your admin. If you have one, sign in; if not, ask them for a setup link.</p>
            <div className="flex flex-wrap justify-center gap-3 mt-8">
              <Link to="/login" className={`${btnPrimary} px-7 py-3.5`} data-testid="landing-footer-signin">Sign in <ArrowRight size={16} weight="bold" /></Link>
            </div>
          </div>
        </section>
      </main>

      <footer className="relative z-10 border-t border-[var(--o-border)]">
        <div className="max-w-6xl px-5 sm:px-10 py-6 flex flex-wrap items-center justify-between gap-3 font-mono text-[11px] text-[var(--o-muted3)]">
          <span>GoNav.Tech · Observer</span>
          <div className="flex gap-4">
            <a href="#how" className="hover:text-[var(--o-fg)]">How it works</a>
            <Link to="/privacy" className="hover:text-[var(--o-fg)]">Privacy</Link>
            <Link to="/login" className="hover:text-[var(--o-fg)]">Sign in</Link>
          </div>
        </div>
      </footer>
    </div>
  );
}
